<template>
  <q-page class="changelogs">
    <KdsNav
      :logo-label="t('app.name')"
      :links="navLinks"
      :cta-secondary="t('landing.nav.logIn')"
      @cta-secondary-click="goAuth"
    />

    <section class="changelogs__header">
      <KdsEyebrow class="changelogs__eyebrow">{{ t('changelogs.eyebrow') }}</KdsEyebrow>
      <h1 class="changelogs__title">{{ t('changelogs.title') }}</h1>
      <p class="changelogs__sub">{{ t('changelogs.subtitle') }}</p>
      <div v-if="currentVersion" class="changelogs__current">
        <KdsEyebrow>{{ t('changelogs.currentVersion', { version: currentVersion }) }}</KdsEyebrow>
      </div>
    </section>

    <section class="changelogs__list" aria-label="Release history">
      <TransitionGroup name="entry-list" tag="div" class="changelogs__list-inner">
        <ChangelogEntryCard
          v-for="(entry, i) in versions"
          :key="entry.version"
          :version="entry.version"
          :type="entry.type"
          :category="entry.category"
          :date="entry.date"
          :title="entry.title"
          :summary="entry.summary"
          :highlights="entry.highlights"
          :image-url="entry.imageUrl"
          :image-alt="entry.imageAlt"
          :locale="locale"
          :index="i"
        />
      </TransitionGroup>

      <p v-if="versions.length === 0" class="changelogs__empty">
        {{ t('changelogs.empty') }}
      </p>
    </section>

    <KdsFooter
      :brand="t('app.name')"
      :status-label="t('landing.footer.status')"
      :status-to="'/status'"
      :columns="footerColumns"
    />
  </q-page>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { KdsNav, KdsEyebrow } from '@/kds';
import KdsFooter from '@/kds/components/KdsFooter.vue';
import { KdsChangelogEntryCard as ChangelogEntryCard } from '@/kds';
import { useChangelog } from '@/composables/useChangelog';
import { useConsent } from '@/composables/useConsent';
import { useSeoMeta } from '@/composables/useSeoMeta';

const { t, locale } = useI18n();
const router = useRouter();
useSeoMeta('changelogs');

const { versions, currentVersion } = useChangelog();
const { openPreferences } = useConsent();

const navLinks = computed(() => [
  { label: t('landing.nav.product'), href: '/#how' },
  { label: t('landing.nav.protocols'), href: '/#protocols' },
  { label: t('landing.nav.pricing'), href: '/#pricing' },
  { label: t('landing.nav.docs'), href: '/docs' },
  { label: t('landing.nav.changelog'), href: '/changelogs' }
]);

const footerColumns = computed(() => [
  {
    title: t('landing.footer.productTitle'),
    links: [
      { label: t('landing.footer.linkOverview'), href: '/#how' },
      { label: t('landing.footer.linkProtocols'), href: '/#protocols' },
      { label: t('landing.footer.linkPricing'), href: '/#pricing' }
    ]
  },
  {
    title: t('landing.footer.resourcesTitle'),
    links: [
      { label: t('landing.footer.linkDocs'), to: '/docs' },
      { label: t('landing.footer.linkChangelog'), to: '/changelogs' },
      { label: t('landing.footer.linkStatus'), to: '/status' }
    ]
  },
  {
    title: t('landing.footer.companyTitle'),
    links: [
      { label: t('landing.footer.linkAbout'), to: '/about' },
      { label: t('landing.footer.linkContact'), to: { path: '/about', hash: '#contact' } }
    ]
  },
  {
    title: t('landing.footer.legalTitle'),
    links: [
      { label: t('landing.footer.linkPrivacy'), to: '/privacy' },
      { label: t('landing.footer.linkTerms'), to: '/terms' },
      { label: t('landing.footer.linkCookies'), to: '/cookies' },
      { label: t('landing.footer.linkLegalNotice'), to: '/legal-notice' },
      { label: t('landing.footer.manageCookies'), action: openPreferences }
    ]
  }
]);

const goAuth = (): void => {
  void router.push({ name: 'auth' });
};

let previousTitle = '';

onMounted(() => {
  if (typeof document !== 'undefined') {
    previousTitle = document.title;
    document.title = `${t('changelogs.title')} — ${t('app.name')}`;
  }
});

onBeforeUnmount(() => {
  if (typeof document !== 'undefined' && previousTitle) {
    document.title = previousTitle;
  }
});
</script>

<style lang="scss" scoped>
.changelogs {
  min-height: 100vh;
  background: var(--paper);
  color: var(--ink);
  padding-bottom: var(--space-7);
}

.changelogs__header {
  max-width: 720px;
  margin: 0 auto;
  padding: var(--space-11) var(--space-6) var(--space-9);
  text-align: center;
}

.changelogs__eyebrow {
  margin-bottom: var(--space-3);
}

.changelogs__title {
  font-size: clamp(32px, 5vw, 56px);
  font-weight: var(--fw-bold);
  line-height: 1.1;
  letter-spacing: -0.02em;
  margin: var(--space-2) 0 var(--space-3);
  color: var(--ink);
}

.changelogs__sub {
  font-size: var(--fs-body);
  line-height: var(--lh-body);
  color: var(--stone);
  margin: 0;
}

.changelogs__current {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  margin-top: var(--space-5);
}

.changelogs__list {
  max-width: 720px;
  margin: 0 auto;
  padding: 0 var(--space-6) var(--space-7);
}

.changelogs__list-inner {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.changelogs__empty {
  text-align: center;
  color: var(--stone);
  font-size: var(--fs-body);
  padding: var(--space-9) 0;
}

// TransitionGroup stagger animation
.entry-list-enter-active {
  transition:
    opacity var(--d-base) var(--ease-out) calc(var(--i, 0) * 50ms),
    transform var(--d-base) var(--ease-out) calc(var(--i, 0) * 50ms);
}

.entry-list-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.entry-list-leave-active {
  transition: opacity var(--d-fast) var(--ease-out);
}

.entry-list-leave-to {
  opacity: 0;
}

@media (max-width: 600px) {
  .changelogs__header {
    padding: var(--space-9) var(--space-4) var(--space-7);
  }

  .changelogs__list {
    padding: 0 var(--space-4) var(--space-7);
  }
}

@media (prefers-reduced-motion: reduce) {
  .entry-list-enter-active,
  .entry-list-leave-active {
    transition: none;
  }
}
</style>
